import { useStore } from '@nanostores/react';
import { billing } from '@/services/billing';
import { formatPeriodRange, knownBoundary, periodDays } from '../utils/billing-period';

export type BillingPeriod = {
  /** Period start, as unix seconds. */
  start?: number;
  /** Period end, as unix seconds. */
  end?: number;
  /** When the period is charged, as unix seconds. */
  chargeDate?: number;
  /** "August 7 – August 31, 2026". Null until both boundaries are known. */
  range: string | null;
  /** Counts the first day of the period as day 1. */
  dayNumber?: number;
  totalDays?: number;
  /** False until both boundaries are known. Cards wait for this before they fetch. */
  isResolved: boolean;
};

/**
 * The window the billing tabs report on: the current billing period.
 *
 * The tabs have no range control, so the period is read rather than chosen. They take it from here
 * instead of reading the billing store themselves, so they all cover the same window, and one change
 * swaps in a user-chosen range later. Each tab writes its own copy around these values.
 */
export function useBillingPeriod(): BillingPeriod {
  const start = useStore(billing.billingPeriodStart);
  const end = useStore(billing.billingPeriodEnd);
  const chargeDate = useStore(billing.nextChargeDate);

  const range = formatPeriodRange(start, end);
  const days = periodDays(start, chargeDate);

  return {
    // Normalised, so a card cannot meter from the epoch on an account that reports 0.
    start: knownBoundary(start),
    end: knownBoundary(end),
    chargeDate,
    range,
    dayNumber: days?.dayNumber,
    totalDays: days?.totalDays,
    isResolved: range != null,
  };
}
